import type { TagCategoriesConfig } from '@racemap/sdk/schema/events';
import type { Immutable } from 'immer';
import { NULL_TAG } from '../../consts/events';

/**
 * Builds a map of available tags with their counts across starters.
 *
 * Collects all tags from starters, filters out invisible tags based on config,
 * sorts tags by their configured index, and counts occurrences of each tag value.
 *
 * @param starters - Array of starters containing tags as Map<string, string>
 * @param tagCategoriesConfig - Tag categories configuration defining visibility and order
 * @returns Map where keys are tag category names and values are arrays of [tagValue, count] tuples,
 *          sorted alphabetically by tag value
 */
export function buildAvailableTagsWithCount(
  starters: Array<{ tags: Map<string, string> }>,
  tagCategoriesConfig: Immutable<TagCategoriesConfig>,
): Map<string, Array<[string, number]>> {
  const tagKeys = new Set<string>();
  for (const starter of starters) {
    for (const key of starter.tags.keys()) {
      const configEntry = tagCategoriesConfig?.[key];
      if (configEntry != null && !configEntry.visible) {
        continue;
      }
      tagKeys.add(key);
    }
  }

  // sort tag keys by index in config
  const sortedTagKeys = Array.from(tagKeys).sort((a, b) => {
    const indexA = tagCategoriesConfig?.[a]?.index ?? Number.POSITIVE_INFINITY;
    const indexB = tagCategoriesConfig?.[b]?.index ?? Number.POSITIVE_INFINITY;
    return indexA - indexB || a.localeCompare(b);
  });

  const result = new Map<string, Array<[string, number]>>();
  for (const key of sortedTagKeys) {
    const counts = new Map<string, number>();
    for (const starter of starters) {
      const value = starter.tags.get(key) ?? NULL_TAG;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    result.set(
      key,
      Array.from(counts.entries()).sort(([valueA], [valueB]) => valueA.localeCompare(valueB)),
    );
  }
  return result;
}
